import React, { useEffect, useRef, useState } from 'react';
import { useUserStore } from '../store';
import { Region } from '../types';

// Переключатель региона в шапке каталога — тот же вид, что на сайте:
// кнопка с флагом и выпадающий список под ней.

const FlagTR: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <rect width="18" height="17" rx="4" fill="#E30917" />
    <g transform="translate(0 -0.5) scale(0.5)">
      <path fill="#EEE" d="M16 24a6 6 0 1 1 0-12c1.31 0 2.52.425 3.507 1.138A7.332 7.332 0 0 0 14 10.647A7.353 7.353 0 0 0 6.647 18A7.353 7.353 0 0 0 14 25.354c2.195 0 4.16-.967 5.507-2.492A5.963 5.963 0 0 1 16 24zm3.913-5.77l2.44.562l.22 2.493l1.288-2.146l2.44.561l-1.644-1.888l1.287-2.147l-2.303.98l-1.644-1.889l.22 2.494z" />
    </g>
  </svg>
);

const FlagIN: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <defs>
      <clipPath id="miniRegionFlagIN">
        <rect width="18" height="17" rx="4" />
      </clipPath>
    </defs>
    <g clipPath="url(#miniRegionFlagIN)">
      <rect width="18" height="5.67" fill="#F93" />
      <rect y="5.67" width="18" height="5.67" fill="#EEE" />
      <rect y="11.33" width="18" height="5.67" fill="#138808" />
      <g transform="translate(0 -0.5) scale(0.5)">
        <circle fill="navy" cx="18" cy="18" r="4" />
        <circle fill="#EEE" cx="18" cy="18" r="3" />
        <path fill="#6666B3" d="M18 15l.146 2.264l1.001-2.035l-.73 2.147l1.704-1.498l-1.497 1.705l2.147-.731l-2.035 1.002L21 18l-2.264.146l2.035 1.001l-2.147-.73l1.497 1.704l-1.704-1.497l.73 2.147l-1.001-2.035L18 21l-.146-2.264l-1.002 2.035l.731-2.147l-1.705 1.497l1.498-1.704l-2.147.73l2.035-1.001L15 18l2.264-.146l-2.035-1.002l2.147.731l-1.498-1.705l1.705 1.498l-.731-2.147l1.002 2.035z" />
        <circle fill="navy" cx="18" cy="18" r="1" />
      </g>
    </g>
  </svg>
);

const FlagUA: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <defs><clipPath id="miniRegionFlagUA"><rect width="18" height="17" rx="4" /></clipPath></defs>
    <g clipPath="url(#miniRegionFlagUA)">
      <rect width="18" height="8.5" fill="#005BBB" />
      <rect y="8.5" width="18" height="8.5" fill="#FFD500" />
    </g>
  </svg>
);

const FLAGS: Record<Region, React.FC<{ size?: number }>> = { TR: FlagTR, IN: FlagIN, UA: FlagUA };

const REGIONS: Array<{ code: Region; name: string }> = [
  { code: 'TR', name: 'Турция' },
  { code: 'IN', name: 'Индия' },
  { code: 'UA', name: 'Украина (СНГ)' },
];

export const RegionSwitcher: React.FC = () => {
  const region = useUserStore((s) => s.lastSelectedRegion);
  const setRegion = useUserStore((s) => s.setLastSelectedRegion);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  const current = REGIONS.find((r) => r.code === region) || REGIONS[0];
  const CurrentFlag = FLAGS[current.code];

  const handleSelect = (code: Region) => {
    try {
      (window as any).Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
    } catch { /* ignore */ }
    setRegion(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 transition-all active:scale-[0.96]"
        style={{ padding: '7px 8px', borderRadius: '14px', background: '#F4F4F7' }}
        aria-label="Выбрать регион"
      >
        <CurrentFlag size={22} />
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="#8B8E97"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full right-0 mt-1.5 rounded-[14px] border border-gray-200/80 z-30 min-w-[170px] animate-fade-in overflow-hidden"
          style={{ background: '#FFFFFF' }}
        >
          {REGIONS.map((r) => {
            const Flag = FLAGS[r.code];
            const active = region === r.code;
            return (
              <button
                key={r.code}
                onClick={() => handleSelect(r.code)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${
                  active ? 'bg-primary-50 text-primary font-semibold' : 'text-text-primary'
                }`}
              >
                <Flag size={22} />
                <span>{r.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
